import { memo } from "react";

import { AvailablePaymentCurrency } from "@simplyk/common";
import { type Control, useWatch } from "react-hook-form";

import { AuctionRateItem } from "./AuctionRateItem";

import { useFrontendAuctionContext } from "@/common/contexts/FrontendAuctionContext";
import { RateOutput } from "@/common/types/trpc";

interface AuctionRateItemContainerProps {
  rate: RateOutput;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  formCurrency: AvailablePaymentCurrency;
  maximumPurchasableTicketsForRate: number;
  ticketingIsClosed: boolean;
  onClickRate: (rateId: string) => void;
}

const AuctionRateItemContainerInner = ({
  rate,
  control,
  formCurrency,
  maximumPurchasableTicketsForRate,
  ticketingIsClosed,
  onClickRate,
}: AuctionRateItemContainerProps) => {
  const { activeAutoBidRateIds, activeAutoBidMaxByRateId, highestBidsMapByRateId } = useFrontendAuctionContext();

  const bidUserAmount =
    (useWatch({ control, name: `ticketsPurchased.${rate.sortIndex}.bid` }) as number | undefined) ?? 0;
  const autoBidMaxAmount = useWatch({
    control,
    name: `ticketsPurchased.${rate.sortIndex}.autoBidMaxAmount`,
  }) as number | undefined;

  const hasLocalAutoBid = Boolean(autoBidMaxAmount && autoBidMaxAmount > 0);
  const serverAutoBidMax = activeAutoBidMaxByRateId[rate.id];
  const currentHighestBid = highestBidsMapByRateId?.[rate.id]?.amount ?? 0;
  const isServerAutoBidStillActive =
    activeAutoBidRateIds.includes(rate.id) && Boolean(serverAutoBidMax && serverAutoBidMax > currentHighestBid);
  const hasServerAutoBid = isServerAutoBidStillActive;

  return (
    <AuctionRateItem
      rate={rate}
      formCurrency={formCurrency}
      maximumPurchasableTicketsForRate={maximumPurchasableTicketsForRate}
      localBidAmountInCents={hasLocalAutoBid ? autoBidMaxAmount : bidUserAmount > 0 ? bidUserAmount : undefined}
      autoBidMaxAmountInCents={hasServerAutoBid ? serverAutoBidMax : undefined}
      isAutoBidBadge={hasLocalAutoBid || hasServerAutoBid}
      hasServerAutoBid={hasServerAutoBid}
      ticketingIsClosed={ticketingIsClosed}
      onClickRate={onClickRate}
    />
  );
};

export const AuctionRateItemContainer = memo(AuctionRateItemContainerInner);
AuctionRateItemContainer.displayName = "AuctionRateItemContainer";
